import React from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import liff from "../../../lib/liff";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUrlParams } from "hooks/useUrlParams";


import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";
import MyPageCard from "default/components/MyPageCard";
import StyledButton from "default/components/StyledButton";

import PotionLayout from "../components/PotionLayout";
import Error from "default/components/Error";
import TicketNotFound from "default/components/TicketNotFound";
import { useTheme } from "@emotion/react";
import { usePotionAssets } from "../hooks/usePotionAssets";
import { usePotionTicketState } from "../hooks/usePotionTicketState";
import { usePotionResetMemory } from "../hooks/usePotionResetMemory";
import PotionWalletCard from "../components/PotionWalletCard";
import { getRequestFormUrl } from "constants/links";

// Helper functions
const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

// Component for character situation (button + dialog)
const CharacterSituationSection = ({ t, work, character, isProcessing }) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  return (
    <>
      <StyledButton
        variant="contained"
        onClick={() => setOpen(true)}
        sx={{
          marginTop: 4,
          background: isProcessing ? "white" : "transparent",
        }}
      >
        {t("common:button.character_situation")}
      </StyledButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "88%",
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(19, 67, 58, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            boxShadow:
              "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
            borderRadius: "8px",
            padding: "32px 24px",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "8px",
              color: "#FFFFFF",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontFamily: theme.system.fonts.heading,
              fontSize: "16px",
              fontWeight: "bold",
              color: "#FFFFFF",
              mb: 3,
              textAlign: "center",
            }}
          >
            {t(`${work}:characterSituation.${character}.title`)}
          </Typography>
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.6,
              color: "#FFFFFF",
              textAlign: "center",
            }}
          >
            {t(`${work}:characterSituation.${character}.description`)}
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

// Potion styled dialog (consistent with CharacterSituationSection)
const PotionDialog = ({ open, onClose, title, children, theme }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        backgroundColor: "transparent",
        boxShadow: "none",
        width: "88%",
      },
    }}
  >
    <Box
      sx={{
        backgroundColor: "rgba(19, 67, 58, 0.95)",
        border: "1px solid rgba(255, 255, 255, 0.6)",
        boxShadow:
          "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
        borderRadius: "8px",
        padding: "32px 24px",
        position: "relative",
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: "8px",
          top: "8px",
          color: "#FFFFFF",
        }}
      >
        <CloseIcon />
      </IconButton>
      <Typography
        variant="h6"
        sx={{
          fontFamily: theme.system.fonts.heading,
          fontSize: "16px",
          fontWeight: "bold",
          color: "#FFFFFF",
          mb: 3,
          textAlign: "center",
          whiteSpace: "pre-line",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  </Dialog>
);

// Component for reset memory (button + dialogs)
const ResetMemorySection = ({ isExpired, resetMemory, t, isProcessing, theme }) => {
  const [openConfirm, setOpenConfirm] = React.useState(false);

  if (isExpired) return null;

  const handleConfirmReset = async () => {
    setOpenConfirm(false);
    await resetMemory.resetMemory();
  };

  return (
    <>
      <StyledButton
        variant="contained"
        disabled={isProcessing || resetMemory.isProcessing}
        onClick={() => setOpenConfirm(true)}
        sx={{
          marginTop: 4,
          background: isProcessing ? "white" : "transparent",
        }}
      >
        {t("common:button.reset_memories")}
      </StyledButton>

      {/* Reset confirmation dialog */}
      <PotionDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={"キャラクターの記憶を\nリセットしますか？"}
        theme={theme}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {t("common:button.warning.reset_memories", { returnObjects: true }).map(
            (warning, i) => (
              <Typography
                key={i}
                sx={{
                  fontFamily: theme.system.fonts.body,
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.8)",
                  textAlign: "center",
                  lineHeight: 1.5,
                }}
              >
                {warning}
              </Typography>
            )
          )}
        </Box>
        <StyledButton
          variant="contained"
          onClick={handleConfirmReset}
          disabled={resetMemory.isProcessing}
          sx={{
            width: "100%",
            border: "1.5px solid rgba(255,255,255,0.6)",
            color: "#FFFFFF",
            fontSize: "14px",
          }}
        >
          {t("common:button.reset")}
        </StyledButton>
      </PotionDialog>

      {/* Reset error dialog */}
      <PotionDialog
        open={resetMemory.isError}
        onClose={resetMemory.clearError}
        title={t("common:button.dialog.reset_memories_fail")}
        theme={theme}
      >
        <StyledButton
          variant="contained"
          onClick={resetMemory.clearError}
          sx={{
            width: "100%",
            border: "1.5px solid rgba(255,255,255,0.6)",
            color: "#FFFFFF",
            fontSize: "14px",
          }}
        >
          {t("common:button.close")}
        </StyledButton>
      </PotionDialog>
    </>
  );
};

function MyPage() {
  const navigate = useNavigate();
  const { soulId, lng, work, character } = useUrlParams();
  const { t } = useTranslation(["common", work]);

  const { ticketState, isLoading, isError, hasPurchased } =
    usePotionTicketState(soulId);
  const assets = usePotionAssets(soulId);
  const resetMemory = usePotionResetMemory();
  const theme = useTheme();
  const requestFormUrl = getRequestFormUrl(lng);

  // Build composite object for MyPageCard (shared component expects userEpisode shape)
  const cardData = ticketState
    ? {
        ...ticketState,
        expire_at: ticketState.expires_at,
        name: t(`${work}:character.${character}`),
        description: t(`${work}:characterSituation.${character}.description`),
        eye_catch_url: `${process.env.PUBLIC_URL}/potion/character_my_page.webp`,
      }
    : null;

  // Navigation handlers
  const handleNavigateQuests = () => {
    navigate(withLng(`/${work}/quests?soul_id=${soulId}`, lng));
  };

  const handleNavigateEmailSettings = () => {
    navigate(withLng(`/${work}/email-settings?soul_id=${soulId}`, lng));
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestForm = () => {
    window.open(requestFormUrl, "_blank");
  };

  if (isLoading) {
    return (
      <PotionLayout>
        <Loading />
      </PotionLayout>
    );
  }

  if (isError) {
    return (
      <PotionLayout>
        <Error />
      </PotionLayout>
    );
  }

  if (!hasPurchased) {
    return (
      <PotionLayout>
        <TicketNotFound onClick={() => navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng))} />
      </PotionLayout>
    );
  }

  return (
    <PotionLayout>
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            marginBottom: 2,
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.sectionHeader,
            })}
          >
            My <span style={{ color: "#8CB808" }}>P</span>age
          </Typography>
        </Box>

        <MyPageCard
          userEpisode={cardData}
          soulName={character}
          displayRelationship={false}
        />

        <PotionWalletCard
          potions={assets.potions}
          tamil={assets.tamil}
          isLoading={assets.isLoading}
          isError={assets.isError}
        />

        {/* 1. キャラクターの状況について */}
        <CharacterSituationSection
          t={t}
          work={work}
          character={character}
          isProcessing={resetMemory.isProcessing}
        />

        {/* 2. クエスト一覧 */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={handleNavigateQuests}
        >
          {t("common:button.quests")}
        </StyledButton>

        {/* 3. タミルを支払う */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={() => navigate(withLng(`/${work}/payment?soul_id=${soulId}`, lng))}
        >
          {t("potion:Footer.payment")}
        </StyledButton>

        {/* 4. チケット購入 */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={() => navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng))}
        >
          {t("potion:Footer.purchase_ticket")}
        </StyledButton>

        {/* 5. 記憶をリセットする */}
        <ResetMemorySection
          isExpired={ticketState.is_expired}
          resetMemory={resetMemory}
          t={t}
          isProcessing={false}
          theme={theme}
        />

        {/* 4. メール設定 */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4 }}
          onClick={handleNavigateEmailSettings}
        >
          {t("common:button.email_settings")}
        </StyledButton>

        {requestFormUrl && (
          <StyledButton
            variant="contained"
            sx={{ mt: 4 }}
            onClick={handleRequestForm}
          >
            {t("common:button.request_form")}
          </StyledButton>
        )}

        {/* 5. チャットに戻る */}
        <StyledButton
          variant="contained"
          sx={{ mt: 4, mb: 4 }}
          onClick={handleCloseWindow}
        >
          {t("common:button.back_to_chat")}
        </StyledButton>
      </Box>
    </PotionLayout>
  );
}

export default MyPage;
